/**
 * Date Filter Context
 * Manages the selected date filter across the application
 * Supports date range: June 1, 2025 - December 31, 2025
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

// Date range constraints
export const DATE_RANGE = {
  minDate: new Date('2025-06-01'),
  maxDate: new Date('2025-12-31'),
  defaultDate: new Date('2025-12-15'),
};

interface DateFilterContextType {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  minDate: Date;
  maxDate: Date;
  formatDate: (date: Date) => string;
  getMonthFromDate: (date: Date) => string;
}

const DateFilterContext = createContext<DateFilterContextType | undefined>(undefined);

export const DateFilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(DATE_RANGE.defaultDate);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get the month string (e.g., "2025-06") from a date for data lookup
  const getMonthFromDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const handleSetSelectedDate = (date: Date) => {
    // Clamp date within valid range
    if (date < DATE_RANGE.minDate) {
      setSelectedDate(DATE_RANGE.minDate);
    } else if (date > DATE_RANGE.maxDate) {
      setSelectedDate(DATE_RANGE.maxDate);
    } else {
      setSelectedDate(date);
    }
  };

  return (
    <DateFilterContext.Provider
      value={{
        selectedDate,
        setSelectedDate: handleSetSelectedDate,
        minDate: DATE_RANGE.minDate,
        maxDate: DATE_RANGE.maxDate,
        formatDate,
        getMonthFromDate,
      }}
    >
      {children}
    </DateFilterContext.Provider>
  );
};

export const useDateFilter = (): DateFilterContextType => {
  const context = useContext(DateFilterContext);
  if (!context) {
    throw new Error('useDateFilter must be used within a DateFilterProvider');
  }
  return context;
};

export default DateFilterContext;
